/**
 * RenderSystem — Canvas 2D entity renderer
 *
 * Draws all entities with 'position' + 'render' components.
 * Supports shapes: circle, square, triangle, diamond, hexagon, star
 * Bloom via shadowBlur + globalCompositeOperation
 * Health bars for enemies
 * Virtual joystick overlay
 * Grid background
 */

export class RenderSystem {
  draw(ctx, entities, engine) {
    const w = engine.width;
    const h = engine.height;

    // Draw background grid
    this._drawGrid(ctx, w, h, engine.elapsed);

    // Draw all renderable entities
    const renderables = entities.query('position', 'render');

    for (const id of renderables) {
      const pos = entities.getComponent(id, 'position');
      const render = entities.getComponent(id, 'render');
      const health = entities.getComponent(id, 'health');

      ctx.save();
      ctx.translate(pos.x, pos.y);

      if (pos.angle && !entities.hasTag(id, 'pickup')) {
        ctx.rotate(pos.angle);
      }

      // Glow effect
      if (render.glow) {
        ctx.shadowColor = render.color;
        ctx.shadowBlur = render.size * 2;
      }

      ctx.fillStyle = render.color;
      ctx.strokeStyle = render.color;
      ctx.lineWidth = 1.5;

      this._drawShape(ctx, render.shape, render.size);

      ctx.shadowBlur = 0;

      // Player i-frame blink
      if (entities.hasTag(id, 'player')) {
        // Draw player glow ring
        ctx.strokeStyle = render.color;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.3 + Math.sin(engine.elapsed * 4) * 0.15;
        ctx.beginPath();
        ctx.arc(0, 0, render.size + 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Health bar for enemies
      if (health && entities.hasTag(id, 'enemy') && health.current < health.max) {
        ctx.rotate(-(pos.angle || 0)); // unrotate for horizontal bar
        const barW = render.size * 2;
        const barH = 3;
        const barY = -render.size - 8;
        ctx.fillStyle = '#333';
        ctx.fillRect(-barW / 2, barY, barW, barH);
        ctx.fillStyle = render.color;
        ctx.fillRect(-barW / 2, barY, barW * (health.current / health.max), barH);
      }

      ctx.restore();
    }

    // Draw pickup pulse animation
    const pickups = entities.queryTag('pickup');
    for (const id of pickups) {
      if (!entities.isAlive(id)) continue;
      const pos = entities.getComponent(id, 'position');
      const render = entities.getComponent(id, 'render');
      const pulse = 1 + Math.sin(engine.elapsed * 6) * 0.3;

      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.rotate(engine.elapsed * 2);
      ctx.globalAlpha = 0.6;
      ctx.shadowColor = render.color;
      ctx.shadowBlur = 8;
      ctx.fillStyle = render.color;
      this._drawShape(ctx, render.shape, render.size * pulse);
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // Draw virtual joystick
    const jsVisual = engine.input.getJoystickVisual();
    if (jsVisual) {
      this._drawJoystick(ctx, jsVisual);
    }

    // Draw flash overlay
    engine.juice.drawFlash(ctx, w, h);
  }

  _drawShape(ctx, shape, size) {
    switch (shape) {
      case 'circle':
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fill();
        break;

      case 'square':
        ctx.fillRect(-size, -size, size * 2, size * 2);
        break;

      case 'triangle':
        ctx.beginPath();
        ctx.moveTo(size, 0);
        ctx.lineTo(-size * 0.7, -size * 0.7);
        ctx.lineTo(-size * 0.7, size * 0.7);
        ctx.closePath();
        ctx.fill();
        break;

      case 'diamond':
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size * 0.6, 0);
        ctx.lineTo(0, size);
        ctx.lineTo(-size * 0.6, 0);
        ctx.closePath();
        ctx.fill();
        break;

      case 'hexagon':
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI * 2) / 6 - Math.PI / 6;
          const method = i === 0 ? 'moveTo' : 'lineTo';
          ctx[method](Math.cos(a) * size, Math.sin(a) * size);
        }
        ctx.closePath();
        ctx.fill();
        break;

      case 'star':
        ctx.beginPath();
        for (let i = 0; i < 10; i++) {
          const a = (i * Math.PI * 2) / 10 - Math.PI / 2;
          const r = i % 2 === 0 ? size : size * 0.5;
          const method = i === 0 ? 'moveTo' : 'lineTo';
          ctx[method](Math.cos(a) * r, Math.sin(a) * r);
        }
        ctx.closePath();
        ctx.fill();
        break;

      default:
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fill();
    }
  }

  _drawGrid(ctx, w, h, elapsed) {
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 212, 255, 0.06)';
    ctx.lineWidth = 0.5;

    const gridSize = 50;
    const offsetX = (elapsed * 10) % gridSize;
    const offsetY = (elapsed * 5) % gridSize;

    for (let x = -gridSize + offsetX; x < w + gridSize; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = -gridSize + offsetY; y < h + gridSize; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  _drawJoystick(ctx, js) {
    ctx.save();
    ctx.globalAlpha = 0.3;

    // Base circle
    ctx.strokeStyle = '#00d4ff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(js.baseX, js.baseY, js.radius, 0, Math.PI * 2);
    ctx.stroke();

    // Stick
    ctx.fillStyle = '#00d4ff';
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.arc(js.stickX, js.stickY, 15, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}
